import type { ApolloError } from '@apollo/client';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import {
  BarChartDatum,
  Button,
  Heading,
  LinearGaugeChart,
  Surface,
  Text,
} from '@quality24/design-system';
import classNames from 'classnames';
import * as React from 'react';

import { DateFilter, GaugeChartComponentData } from '../../../../@types';

import { parseDataValue } from '../../../../utils/dataUtils';
import { filterData } from '../../utils/filter';
import { mapData } from '../../utils/mapper';
import { createReferenceMap } from '../../utils/reference';
import { formatChartData, getAdditionalChartKeys } from './utils';

import styles from '../../DataViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, Array<Record<string, unknown>>>;
  dateFilter: DateFilter;
  renderOnSurface?: boolean;
  config: Omit<GaugeChartComponentData, 'type'>;
}

/**
 * <GaugeChartContainer> page
 */
export const GaugeChartContainer: React.FunctionComponent<Props> = ({
  className,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  renderOnSurface = true,
  config: {
    title,
    className: configClassName,
    options,
    postQuery,
    rootKey,
    aggrKey,
    references,
    groupBy,
    surfaceClassName,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as BarChartDatum[],
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  // Get the chart keys
  const keys = React.useMemo(() => getAdditionalChartKeys(groupBy), [groupBy]);

  /**
   * Post-processing - Calculate percentages
   */
  const chartData = React.useMemo(
    () => formatChartData(aggrKey, mappedData, keys),
    [aggrKey, mappedData, keys],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('GaugeChartContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      chartData,
      groupKeys: keys,
    });
  }

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, surfaceClassName, {
          'on-surface': renderOnSurface,
        })}
        padding="sm"
        backgroundColor="grayLight2"
      >
        {title && (
          <Heading as="h2" size="sm2" weight="bold" className="mb-4">
            {!loading && parseDataValue(data, title, 'string')}
          </Heading>
        )}

        {!loading && !error && aggrData.length === 0 && (
          <Text size="sm2" color="gray100">
            Não há dados disponíveis.
          </Text>
        )}

        {!loading && error && (
          <div className={styles['error-container']}>
            <Text className="mb-2" size="sm2" color="gray100">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {!error && aggrData.length > 0 && (
          <LinearGaugeChart
            loading={loading}
            data={chartData}
            options={{
              axisRight: {
                key: keys.length > 1 ? keys[1] : undefined,
                enabled: keys.length > 1,
              },
              ...options,
            }}
          />
        )}
      </Surface>
    </div>
  );
};

export default GaugeChartContainer;
